import Matter from 'matter-js';
import decomp from 'poly-decomp';
import { PhysicsType, Physics, PhysicsParams, PhysicsPartParams } from './Physics';
import { GameObject } from '@combos-fun/engine';
import { Transform2D } from '@combos-fun/plugin-renderer';

type ShapeParams = PhysicsParams | PhysicsPartParams;

interface ShapeContext {
  x: number
  y: number
  transformWidth: number
  transformHeight: number
  defaultRadius: number
}

interface Vector2 {
  x: number
  y: number
}

const DEFAULT_THICKNESS = 4;
const MIN_SEGMENT = 0.5;

let decompRegistered = false;

function ensureDecomp() {
  if (decompRegistered) {
    return;
  }
  const lib = (decomp as any)?.quickDecomp ? decomp : (decomp as any)?.default;
  if (lib?.quickDecomp) {
    Matter.Common.setDecomp(lib);
  }
  decompRegistered = true;
}

export default class BodiesFactory {
  private Bodies: typeof Matter.Bodies;
  constructor() {
    this.Bodies = Matter.Bodies;
    ensureDecomp();
  }

  public create(component: Physics): Matter.Body {
    const { gameObject, bodyParams } = component;
    const coordinate = this.getCoordinate(gameObject);
    const x = bodyParams.position ? bodyParams.position.x : coordinate.x;
    const y = bodyParams.position ? bodyParams.position.y : coordinate.y;
    const transform = gameObject.getComponent(Transform2D);
    const transformWidth =
      (transform?.size.width ?? 0) * (transform?.scale.x ?? 1);
    const transformHeight =
      (transform?.size.height ?? 0) * (transform?.scale.y ?? 1);
    /** Matter.circle approximates with a polygon; NaN/0 radius breaks vertices. */
    const defaultRadius = Math.max(1, Math.min(transformWidth, transformHeight) / 2);
    const body = this.createShape(bodyParams, {
      x,
      y,
      transformWidth,
      transformHeight,
      defaultRadius,
    }, false);
    if (!body) {
      throw new Error(
        `[plugin-matterjs] Failed to create body for type ${String(bodyParams.type)}`,
      );
    }
    return body;
  }

  private createShape(
    params: ShapeParams,
    ctx: ShapeContext,
    isPart: boolean,
  ): Matter.Body {
    const options = params.bodyOptions;
    switch (params.type) {
      case PhysicsType.RECTANGLE: {
        const width = params.width ?? ctx.transformWidth;
        const height = params.height ?? ctx.transformHeight;
        return this.Bodies.rectangle(ctx.x, ctx.y, width, height, options);
      }
      case PhysicsType.CIRCLE: {
        const radius = params.radius ?? ctx.defaultRadius;
        return this.Bodies.circle(ctx.x, ctx.y, radius, options);
      }
      case PhysicsType.POLYGON: {
        const sides = Math.max(3, params.sides ?? 6);
        const radius = params.radius ?? ctx.defaultRadius;
        return this.Bodies.polygon(ctx.x, ctx.y, sides, radius, options);
      }
      case PhysicsType.TRAPEZOID: {
        const width = params.width ?? ctx.transformWidth;
        const height = params.height ?? ctx.transformHeight;
        const slope = Math.min(Math.max(params.slope ?? 0.5, 0), 0.999);
        return this.Bodies.trapezoid(ctx.x, ctx.y, width, height, slope, options);
      }
      case PhysicsType.FROM_VERTICES:
        return this.createFromVertices(params, ctx, options);
      case PhysicsType.CAPSULE:
        return this.createCapsule(params, ctx, options);
      case PhysicsType.EDGE:
        return this.createEdge(params, ctx, options);
      case PhysicsType.CHAIN:
        return this.createChain(params, ctx, options);
      case PhysicsType.COMPOUND:
        if (isPart) {
          throw new Error('[plugin-matterjs] Nested COMPOUND parts are not supported');
        }
        return this.createCompound(params, ctx, options);
      default:
        throw new Error(
          `[plugin-matterjs] Unsupported PhysicsType: ${String(params.type)}`,
        );
    }
  }

  private createFromVertices(
    params: ShapeParams,
    ctx: ShapeContext,
    options: PhysicsParams['bodyOptions'],
  ): Matter.Body {
    const sets = params.vertexSets ?? (params.vertices ? [params.vertices] : []);
    if (!sets.length || sets.some(set => !set || set.length < 3)) {
      throw new Error(
        '[plugin-matterjs] FROM_VERTICES requires vertices (or vertexSets) with at least 3 points each',
      );
    }
    ensureDecomp();
    const body = this.Bodies.fromVertices(
      ctx.x,
      ctx.y,
      sets as Matter.Vector[][],
      options,
      true,
    );
    if (!body) {
      throw new Error('[plugin-matterjs] FROM_VERTICES produced an empty body');
    }
    return body;
  }

  private createCapsule(
    params: ShapeParams,
    ctx: ShapeContext,
    options: PhysicsParams['bodyOptions'],
  ): Matter.Body {
    const radius = Math.max(1, params.radius ?? ctx.defaultRadius);
    const length = Math.max(radius * 2, params.length ?? ctx.transformWidth);
    const shaft = length - radius * 2;
    if (shaft < MIN_SEGMENT) {
      return this.Bodies.circle(ctx.x, ctx.y, radius, options);
    }
    const parts = [
      this.Bodies.rectangle(ctx.x, ctx.y, shaft, radius * 2),
      this.Bodies.circle(ctx.x - shaft / 2, ctx.y, radius),
      this.Bodies.circle(ctx.x + shaft / 2, ctx.y, radius),
    ];
    return Matter.Body.create({ ...options, parts });
  }

  private createEdge(
    params: ShapeParams,
    ctx: ShapeContext,
    options: PhysicsParams['bodyOptions'],
  ): Matter.Body {
    if (!params.from || !params.to) {
      throw new Error('[plugin-matterjs] EDGE requires from and to');
    }
    const x1 = ctx.x + params.from.x;
    const y1 = ctx.y + params.from.y;
    const x2 = ctx.x + params.to.x;
    const y2 = ctx.y + params.to.y;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.hypot(dx, dy);
    if (len < MIN_SEGMENT) {
      throw new Error('[plugin-matterjs] EDGE from/to are too close');
    }
    const thickness = Math.max(1, params.thickness ?? DEFAULT_THICKNESS);
    const { angle: _ignored, ...rest } = options ?? {};
    return this.Bodies.rectangle((x1 + x2) / 2, (y1 + y2) / 2, len, thickness, {
      ...rest,
      angle: Math.atan2(dy, dx),
    });
  }

  private createChain(
    params: ShapeParams,
    ctx: ShapeContext,
    options: PhysicsParams['bodyOptions'],
  ): Matter.Body {
    const verts = params.vertices;
    if (!verts || verts.length < 2) {
      throw new Error('[plugin-matterjs] CHAIN requires vertices with at least 2 points');
    }
    const thickness = Math.max(1, params.thickness ?? DEFAULT_THICKNESS);
    const parts: Matter.Body[] = [];
    for (let i = 0; i < verts.length - 1; i++) {
      const a = verts[i];
      const b = verts[i + 1];
      const x1 = ctx.x + a.x;
      const y1 = ctx.y + a.y;
      const x2 = ctx.x + b.x;
      const y2 = ctx.y + b.y;
      const dx = x2 - x1;
      const dy = y2 - y1;
      const len = Math.hypot(dx, dy);
      if (len < MIN_SEGMENT) {
        continue;
      }
      parts.push(this.Bodies.rectangle(
        (x1 + x2) / 2,
        (y1 + y2) / 2,
        len,
        thickness,
        { angle: Math.atan2(dy, dx) },
      ));
    }
    if (!parts.length) {
      throw new Error('[plugin-matterjs] CHAIN has no usable segments');
    }
    return Matter.Body.create({ ...options, parts });
  }

  private createCompound(
    params: ShapeParams,
    ctx: ShapeContext,
    options: PhysicsParams['bodyOptions'],
  ): Matter.Body {
    const partParams = (params as PhysicsParams).parts;
    if (!partParams?.length) {
      throw new Error('[plugin-matterjs] COMPOUND requires a non-empty parts array');
    }
    const parts: Matter.Body[] = [];
    for (let i = 0; i < partParams.length; i++) {
      const part = partParams[i];
      const ox = ctx.x + (part.position?.x ?? 0);
      const oy = ctx.y + (part.position?.y ?? 0);
      const partBody = this.createShape(part, { ...ctx, x: ox, y: oy }, true);
      parts.push(...this.asParts(partBody));
    }
    if (!parts.length) {
      throw new Error('[plugin-matterjs] COMPOUND produced no parts');
    }
    return Matter.Body.create({ ...options, parts });
  }

  /** Unwrap a compound wrapper so it can be merged into another parent. */
  private asParts(body: Matter.Body): Matter.Body[] {
    if (body.parts && body.parts.length > 1) {
      return body.parts.slice(1);
    }
    return [body];
  }

  private getCoordinate(gameObject: GameObject): Vector2 {
    const transform = gameObject.getComponent(Transform2D);
    const parentSize = gameObject.parent?.getComponent(Transform2D)?.size;
    const x = (transform?.position.x ?? 0) + (transform?.anchor.x ?? 0) * (parentSize?.width ?? 0);
    const y = (transform?.position.y ?? 0) + (transform?.anchor.y ?? 0) * (parentSize?.height ?? 0);
    return {
      x,
      y,
    };
  }
}
